import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  title?: string;
  description?: string;
  className?: string;
  showClose?: boolean;
}

function Modal({
  open,
  onOpenChange,
  children,
  title,
  description,
  className,
  showClose = true,
}: ModalProps) {
  const { t } = useTranslation();
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild>
              <motion.div
                className={cn(
                  "fixed left-1/2 top-1/2 z-50 w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-card bg-surface p-6 shadow-xl",
                  className,
                )}
                initial={{ opacity: 0, scale: 0.95, x: "-50%", y: "-50%" }}
                animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
                exit={{ opacity: 0, scale: 0.95, x: "-50%", y: "-50%" }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                {title ? (
                  <DialogPrimitive.Title className="text-lg font-semibold text-text-primary">
                    {title}
                  </DialogPrimitive.Title>
                ) : (
                  <DialogPrimitive.Title className="sr-only">{t("common.a11y.dialog")}</DialogPrimitive.Title>
                )}
                {description ? (
                  <DialogPrimitive.Description className="mt-1 text-sm text-text-tertiary">
                    {description}
                  </DialogPrimitive.Description>
                ) : (
                  <DialogPrimitive.Description className="sr-only">{t("common.a11y.dialogContent")}</DialogPrimitive.Description>
                )}
                {showClose && (
                  <DialogPrimitive.Close className="absolute right-4 top-4 rounded-full p-1 text-text-muted hover:bg-surface-tertiary hover:text-text-secondary">
                    <X className="h-4 w-4" />
                    <span className="sr-only">{t("common.a11y.close")}</span>
                  </DialogPrimitive.Close>
                )}
                {children}
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}

export { Modal };
